import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  createMemory,
  deleteMemory,
  getMemorySettings,
  listMemories,
  updateMemory,
  updateMemorySettings,
} from '../services/memoryApi';

/**
 * Things the person wants agents to know about them in every conversation —
 * what they work on, how they want answers written, decisions already made.
 *
 * Two consents, and both are the person's. `memory_enabled` is the master
 * switch — off means nothing is injected into a prompt and nothing new is saved.
 * `auto_capture` is off unless they turn it on, because inferring a fact is a
 * guess and a wrong guess here has none of the corrections a wrong ANSWER has:
 * no thumbs-down, no retry, no next message. It just shapes every later answer
 * from inside the prompt. Anything captured that way is labelled, so what was
 * inferred about someone is always visible to them.
 *
 * The caps come back with the list rather than being duplicated here — discover
 * enforces them, and a second copy would eventually tell people the wrong limit.
 */

// Deliberately NOT a copy of the server's caps. They are unknown until the list response
// carries them; until then the composer stays closed rather than counting against a number
// this file invented, which is the drift the server-supplied caps exist to prevent.
const UNKNOWN = null;

const inputClass =
  'w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none';

const buttonClass =
  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

function Toggle({ checked, onChange, disabled, label, hint }) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        role="switch"
        className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span className="text-xs leading-relaxed text-fg-muted">{hint}</span>
      </span>
    </label>
  );
}

Toggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
};

export default function MemorySection() {
  const { user, getAccessToken, getUserIdDiscover } = useAuth();
  const [memories, setMemories] = useState([]);
  const [max, setMax] = useState(UNKNOWN);
  const [maxChars, setMaxChars] = useState(UNKNOWN);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [consent, setConsent] = useState({
    memory_enabled: true,
    auto_capture: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, settings] = await Promise.all([
        listMemories(getAccessToken(), getUserIdDiscover()),
        getMemorySettings(getAccessToken(), getUserIdDiscover()),
      ]);
      setMemories(data?.items ?? []);
      setMax(data?.max ?? UNKNOWN);
      setMaxChars(data?.max_chars ?? UNKNOWN);
      setConsent(settings);
      // A response that carried rows but no caps is malformed, and saying so beats a
      // composer that silently enforces a limit nobody agreed on.
      setError(
        data?.max && data?.max_chars
          ? ''
          : 'Could not read the saving limits, so new memories are disabled for now.',
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, getUserIdDiscover]);

  useEffect(() => {
    load();
  }, [load]);

  // One wrapper for every write: they differ only in the call, and each needs the
  // same busy flag, the same error surface and the same reload afterwards.
  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const content = draft.trim();
    if (!content) return;
    const ok = await run(() =>
      createMemory(getAccessToken(), getUserIdDiscover(), content, user?.email),
    );
    if (ok) setDraft('');
  };

  const handleSaveEdit = async (id) => {
    const content = editingText.trim();
    if (!content) return;
    const ok = await run(() =>
      updateMemory(getAccessToken(), getUserIdDiscover(), id, content),
    );
    if (ok) setEditingId(null);
  };

  const handleDelete = (id) =>
    run(() => deleteMemory(getAccessToken(), getUserIdDiscover(), id));

  // Deliberately NOT through `run`: that reloads the whole tab afterwards, which for a
  // checkbox is two extra requests, and the reloaded GET would overwrite the very answer
  // this needs to keep. The PUT already returns the resulting pair — turning the master
  // switch off also disarms auto-capture server-side — so that response IS the new state.
  const handleToggle = async (patch) => {
    setBusy(true);
    setError('');
    try {
      setConsent(
        await updateMemorySettings(
          getAccessToken(),
          getUserIdDiscover(),
          patch,
          user?.email,
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Unknown caps close the composer for the same reason a full list does: there is no
  // honest counter to show and no limit to enforce.
  const capsKnown = Boolean(max && maxChars);
  const full = capsKnown && memories.length >= max;
  const off = !consent.memory_enabled;
  const canWrite = capsKnown && !full && !off;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-fg-secondary">
        What agents know about you — your role and what you work on, and how you
        want answers written. Applied quietly rather than repeated back to you.
      </p>

      <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface-sunken p-3">
        <Toggle
          checked={consent.memory_enabled}
          disabled={busy}
          onChange={(v) => handleToggle({ memory_enabled: v })}
          label="Use saved memories"
          hint="When off, nothing here is sent to an agent and nothing new is saved."
        />
        {/* Hidden rather than disabled when the master is off: the server also
            disarms it, so showing an off sub-switch would imply it is merely
            unavailable rather than cleared. */}
        {!off && (
          <Toggle
            checked={consent.auto_capture}
            disabled={busy}
            onChange={(v) => handleToggle({ auto_capture: v })}
            label="Let agents add memories from our conversations"
            hint="Off by default. When on, something you state about yourself may be saved — labelled Auto, and you can edit or delete it."
          />
        )}
      </div>

      <div className="rounded-xl border border-line bg-surface-sunken p-3">
        <textarea
          className={inputClass}
          aria-label="New memory"
          rows={2}
          value={draft}
          maxLength={maxChars ?? undefined}
          disabled={busy || !canWrite}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={
            off
              ? 'Memory is off. Turn it on above to save one.'
              : !capsKnown
                ? 'Loading…'
                : full
                  ? `You've saved the maximum of ${max} memories. Delete one to add another.`
                  : 'e.g. I work on the QSM project — default to it when I do not name one.'
          }
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-fg-muted">
            {capsKnown
              ? `${memories.length} of ${max} saved · ${draft.length}/${maxChars}`
              : `${memories.length} saved`}
          </span>
          <button
            type="button"
            className={`${buttonClass} bg-accent text-on-accent hover:opacity-90`}
            onClick={handleAdd}
            disabled={!draft.trim() || busy || !canWrite}
          >
            <Plus size={13} aria-hidden="true" />
            Save memory
          </button>
        </div>
      </div>

      {/* aria-live so a refusal (too long, duplicate, over the cap) is announced
          rather than only appearing above a list the person is not looking at. */}
      <p aria-live="polite" className="min-h-[1rem] text-xs text-danger">
        {error}
      </p>

      {loading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : memories.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-fg-muted">
          No memories yet. Anything you save appears here, and you can edit or
          delete it at any time.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {memories.map((memory) => (
            <li
              key={memory.id}
              className="flex items-start gap-2 rounded-xl border border-line bg-surface px-3 py-2.5"
            >
              {editingId === memory.id ? (
                <>
                  <textarea
                    className={inputClass}
                    aria-label="Edit memory"
                    rows={2}
                    value={editingText}
                    maxLength={maxChars ?? undefined}
                    onChange={(e) => setEditingText(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`${buttonClass} bg-accent text-on-accent hover:opacity-90`}
                    onClick={() => handleSaveEdit(memory.id)}
                    disabled={!editingText.trim() || busy}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-fg-muted hover:text-fg"
                    onClick={() => setEditingId(null)}
                    aria-label="Cancel editing"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </>
              ) : (
                <>
                  <p className="flex-1 text-sm leading-relaxed text-fg">
                    {/* The slot label, when this is a profile field rather than a loose
                        note: "Role", "Answer format". It is what makes the list read as a
                        profile, and what explains why editing one replaces it. */}
                    {memory.slot_label && (
                      <span className="mr-1.5 font-semibold text-fg-muted">
                        {memory.slot_label}:
                      </span>
                    )}
                    {memory.content}
                    {memory.source === 'auto' && (
                      <span
                        className="ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fg-muted ring-1 ring-line"
                        title="Captured from a conversation. Edit it and it becomes yours."
                      >
                        Auto
                      </span>
                    )}
                  </p>
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-fg-muted hover:text-fg"
                    onClick={() => {
                      setEditingId(memory.id);
                      setEditingText(memory.content);
                    }}
                    aria-label="Edit memory"
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-fg-muted hover:text-danger"
                    onClick={() => handleDelete(memory.id)}
                    disabled={busy}
                    aria-label="Delete memory"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
